import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';

import { User } from 'src/lib/user/User';

import { AuthContext } from '../auth-context';

import type { AuthState } from '../../types';

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });

  const resetAuthState = useCallback(() => {
    setState({ user: null, loading: false });
  }, [setState]);

  const checkUserSession = useCallback(async () => {
    try {
      User.onAuthStateChanged((user) => {
        if (user) {
          setState({ user, loading: false });
        } else {
          resetAuthState();
        }
      });
    } catch (error) {
      console.error(error);
      resetAuthState();
    }
  }, [setState, resetAuthState]);

  useEffect(() => {
    const unsubscribe = User.onAuthStateChanged((user) => {
      if (user) {
        setState({ user, loading: false });
      } else {
        resetAuthState();
      }
    });
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user?.uid,
            displayName: state.user?.displayName,
            role: state.user?.role,
          }
        : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
